import { Currency } from "@redotech/money/currencies";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

import type { Contract, Target } from "../types";
import { moneyPreludeDts, moneyPreludeJs } from "./money-prelude";

/**
 * Input universe for the checkout.shipping-method.custom-rate-table contract.
 *
 * Hands the artifact the cart + addresses + Redo-computed coverage costs.
 * Authors declare a selector to narrow this; the platform prunes the schema
 * before parsing the input so the artifact sees only what it asked for.
 *
 * Deliberately excluded from v1:
 *   - Customer PII (no email, name, customer id, tags).
 *   - Live carrier rates (planned behind a future requiresCarrierRates flag).
 *   - Other rate tables' outputs (cross-table logic lives outside the artifact).
 *   - Cart / checkout token, session id, locale.
 *
 * The `money` helper namespace lives only in the contract's `.d.ts` and is
 * injected at invoke time as a top-level binding — that's why it isn't in
 * this Zod input schema. See runtimePreludeDts / runtimePreludeJs below.
 */

const moneySchema = z.object({
  amount: z.number().finite(),
  currency: createEnum(Currency, { enumName: "Currency" }),
});
type Money = z.infer<typeof moneySchema>;

const weightSchema = z.object({ grams: z.number().nonnegative() });

const lineItemSchema = z.object({
  productId: z.string(),
  variantId: z.string(),
  sku: z.string(),
  title: z.string(),
  productTags: z.array(z.string()),
  vendor: z.string(),
  quantity: z.number().int().nonnegative(),
  unitPrice: moneySchema,
  totalPrice: moneySchema,
  weight: weightSchema,
  requiresShipping: z.boolean(),
  properties: z.array(z.object({ name: z.string(), value: z.string() })),
});

const cartSchema = z.object({
  lineItems: z.array(lineItemSchema),
  subtotal: moneySchema,
  totalWeight: weightSchema,
  itemCount: z.number().int().nonnegative(),
  presentmentCurrency: createEnum(Currency, { enumName: "Currency" }),
  shopCurrency: createEnum(Currency, { enumName: "Currency" }),
});

const destinationSchema = z.object({
  country: z.string(),
  province: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
});

const originSchema = z.object({
  country: z.string(),
  province: z.string().optional(),
});

const coverageSchema = z.object({
  returnCost: moneySchema.nullable(),
  packageProtectionCost: moneySchema.nullable(),
});

export const customRateInputSchema = z.object({
  cart: cartSchema,
  destination: destinationSchema,
  origin: originSchema,
  coverage: coverageSchema,
});

/**
 * Output: the artifact returns either a finished rate (price + names) or null
 * to signal "this table doesn't apply to this cart; skip it." The platform
 * converts `price` to presentment currency at the Shopify boundary. No
 * further rounding is applied — the artifact owns the final price as
 * returned.
 */
export const customRateOutputSchema = z
  .object({
    price: moneySchema.extend({ amount: z.number().finite().nonnegative() }),
    name: z.string().min(1),
    description: z.string().optional(),
  })
  .nullable();
export type CustomRateOutput = z.infer<typeof customRateOutputSchema>;

export const customRateTarget = {
  id: "checkout.shipping-method.custom-rate-table",
  displayName: "Custom shipping method",
  description:
    "Produces a single shipping-method rate at checkout from cart + destination. " +
    "Prefer FIXED or DYNAMIC rate tables when they can express the desired rate; " +
    "CUSTOM adds a per-checkout redojs invocation roundtrip.",
} as const satisfies Target;

export const customRateStarterCode = `\
// Edit this function. It receives the cart + destination and returns either
// a finished rate or null to skip this table.
//
// The \`money\` helper namespace is available as a global. Operations that
// combine two Money values in different currencies will throw, so currency
// bugs surface the first time you test-run the artifact. To combine an amount
// in another currency, run it through \`money.convert(m, currency)\` first.
export default (input: Input): Output => {
  // Flat $5 in the cart's presentment currency.
  return {
    price: { amount: 5, currency: input.cart.presentmentCurrency },
    name: "Custom shipping",
  };
};
`;

export const customRateContractV1: Contract = {
  id: "checkout.shipping-method.custom-rate-table@2026-05.1",
  targetId: customRateTarget.id,
  inputSchema: customRateInputSchema,
  outputSchema: customRateOutputSchema,
  tools: [],
  runtimePreludeJs: moneyPreludeJs(),
  runtimePreludeDts: moneyPreludeDts(),
  sampleInput: {
    cart: {
      lineItems: [
        {
          productId: "prod_1",
          variantId: "var_1",
          sku: "SKU-001",
          title: "Widget",
          productTags: ["fragile"],
          vendor: "Acme",
          quantity: 2,
          unitPrice: { amount: 49.99, currency: Currency.USD },
          totalPrice: { amount: 99.98, currency: Currency.USD },
          weight: { grams: 250 },
          requiresShipping: true,
          properties: [{ name: "engraving", value: "Happy Birthday" }],
        },
      ],
      subtotal: { amount: 99.98, currency: Currency.USD },
      totalWeight: { grams: 500 },
      itemCount: 2,
      presentmentCurrency: Currency.USD,
      shopCurrency: Currency.USD,
    },
    destination: {
      country: "US",
      province: "CA",
      postalCode: "94110",
      city: "San Francisco",
    },
    origin: { country: "US", province: "UT" },
    coverage: {
      returnCost: { amount: 1.5, currency: Currency.USD },
      packageProtectionCost: null,
    },
  } satisfies z.infer<typeof customRateInputSchema>,
};

/**
 * Re-export of the Money type derived from the input schema for callers that
 * want to type-check sample data — kept private otherwise to avoid collision
 * with the ambient `Money` declared in the artifact's d.ts.
 */
export type CustomRateMoney = Money;
